import React, { useEffect, useState } from 'react';
import { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import Link from 'next/link';
import useTranslation from 'next-translate/useTranslation';
import { Card, CardContent } from '@material-ui/core';
import { ChevronLeft } from '@material-ui/icons';
import { Form, Formik } from 'formik';
import axios from 'axios';
import * as yup from 'yup';
import classNames from 'classnames';
import { Auth } from 'aws-amplify';
import { CognitoHostedUIIdentityProvider } from '@aws-amplify/auth';
import { ParsedUrlQuery } from 'querystring';
import planStyle from '../styles/Plan.module.scss';
import generalStyle from '../styles/General.module.scss';
import Head from '../components/Head';
import Typography from '../components/Typography';
import PlanHeader from '../screen/Plan/PlanHeader';
import { colors } from '../config/color';
import { fonts } from '../config/font';
import { accountRoot, loginRoot } from '../config/translationRoots';
import PurchaseSteps from '../components/PurchaseSteps';
import FormikInput from '../components/Formik/FormikInput';
import Button from '../components/Buttons/Button';
import SocialLoginButtons from '../components/SocialLoginButtons';
import iconInfoLogin from '../assets/images/login/info_login.svg';
import sentry from '../utils/sentry';
import AccountExistsDialog from '../components/AccountExistsDialog';
import AccountNotFoundDialog from '../components/AccountNotFoundDialog';
import { PASSWORD_PATTERN } from '../utils/helpers';
import BG from '../components/Background';
import BackButton from '../components/Buttons/BackButton';
import { getLineHeight } from '../utils/getLineHeight';
import Navigationbar from '../components/Navigationbar';
import { loggedInUser } from '../utils/auth';

const { ROOT_LOGIN } = loginRoot;
const { ROOT_PURCHASE_PLAN } = accountRoot;

export const queryGenerator = (query: ParsedUrlQuery) =>
  Object.entries(query)
    .map((val) => val.join('='))
    .join('&');

function SignupAndPurchase() {
  const { t } = useTranslation('common');
  const router = useRouter();
  const {
    query: { redirectUrl, plan: queryPlan, accountExists, accountNotFound },
    query,
  } = router;

  const planName = typeof queryPlan === 'string' ? queryPlan.toUpperCase() : 'PRO';

  const [unauthorizedError, setUnauthorizedError] = useState(false);
  const [unexpectedError, setUnexpectedError] = useState(false);
  const [existsError, setExistsError] = useState(false);

  const [emailSignup, setEmailSignup] = useState(null);

  const loginInitialValues = { email: '', password: '' };
  const loginValidationSchema = yup.object({
    email: yup.string().required(t('purchase.requireEmail')),
    password: yup.string().required(t('purchase.requirePassword')),
  });

  const emailInitialValues = { email: '' };
  const emailValidationSchema = yup.object({
    email: yup.string().email().required(t('purchase.requireEmail')),
  });

  const signupInitialValues = { fullName: '', password: '', confirm: '' };
  const signupValidationSchema = yup.object({
    fullName: yup.string().required(t('purchase.requireName')),
    password: yup
      .string()
      .required(t('purchase.requirePassword'))
      .matches(PASSWORD_PATTERN, t('login.invalidPassword')),
    confirm: yup.string().test('do-passwords-match', t('purchase.requireMatch'), (value, context) => {
      return value === context.parent.password;
    }),
  });

  // if user opens this page w/o params, this will write page url for redirect
  useEffect(() => {
    if (!new URLSearchParams(window.location.search).get('redirectUrl')) {
      router.push(`/signup_and_purchase?redirectUrl=${encodeURIComponent('/account/upgrade')}`);
    }
  }, []);

  const handleLogin = ({ email, password }, { setSubmitting }) => {
    setSubmitting(true);
    Auth.signIn(email.trim().toLowerCase(), password)
      .then(() => {
        if (unauthorizedError) setUnauthorizedError(false);
        if (unexpectedError) setUnexpectedError(false);
      })
      .catch((ex) => {
        setSubmitting(false);
        if (ex.code === 'NotAuthorizedException') {
          setUnauthorizedError(true);
        } else {
          setUnexpectedError(true);
          sentry.error(ex);
        }
      });
  };

  const handleSignup = ({ fullName, password, confirm }, { setSubmitting }) => {
    setSubmitting(true);
    const email = emailSignup.trim().toLowerCase();
    Auth.signUp({
      username: email,
      password,
      attributes: {
        email,
        name: fullName,
        given_name: fullName.split(' ')[0],
        family_name: fullName.split(' ').slice(1).join(' '),
      },
    })
      .then(async (user) => {
        if (user && user.userConfirmed) {
          await Auth.signIn(email, password);
          if (existsError) setExistsError(false);
          if (unexpectedError) setUnexpectedError(false);
        } else {
          throw new Error(`[ERROR] [Cognito] Cannot sign up user ${email}. User data: ${JSON.stringify(user)}`);
        }
      })
      .catch((ex) => {
        setSubmitting(false);
        if (
          ex.code === 'UsernameExistsException' ||
          (ex.code === 'UserLambdaValidationException' && ex.message.includes('User existed'))
        ) {
          setExistsError(true);
        } else {
          setUnexpectedError(true);
          sentry.error(ex);
        }
      });
  };

  const federatedSignIn = async (provider: CognitoHostedUIIdentityProvider, signup: boolean) => {
    try {
      const signupQuery = `signup=${signup}`;
      const customState = `${window.location.search.slice(1)}&${signupQuery}`;
      await Auth.federatedSignIn({ provider, customState });
      if (unexpectedError) setUnexpectedError(false);
    } catch (ex) {
      setUnexpectedError(true);
      sentry.error(ex);
    }
  };

  const googleSignin = () => federatedSignIn(CognitoHostedUIIdentityProvider.Google, false);
  const facebookSignin = () => federatedSignIn(CognitoHostedUIIdentityProvider.Facebook, false);
  const appleSignin = () => federatedSignIn(CognitoHostedUIIdentityProvider.Apple, false);
  const googleSignup = () => federatedSignIn(CognitoHostedUIIdentityProvider.Google, true);
  const facebookSignup = () => federatedSignIn(CognitoHostedUIIdentityProvider.Facebook, true);
  const appleSignup = () => federatedSignIn(CognitoHostedUIIdentityProvider.Apple, true);

  const handleEmail = async ({ email }) => {
    try {
      await axios.get(`/api/email_in_use?email=${encodeURIComponent(email)}`);
      if (existsError) setExistsError(false);
      setEmailSignup(email);
    } catch (ex) {
      if (ex?.response?.status === 400) {
        setExistsError(true);
      } else {
        sentry.error(ex);
      }
    }
  };

  const onBackClick = () => {
    if (emailSignup) return setEmailSignup(null);
    return router.back();
  };

  return (
    <div className={classNames(generalStyle.App, planStyle.app)}>
      {!accountNotFound ? '' : <AccountNotFoundDialog router={router} />}
      {!accountExists ? '' : <AccountExistsDialog router={router} />}
      <Head title={t('appHead.purchasePlan')} />
      <Navigationbar login={true} />
      <div className={planStyle.planContainer}>
        <BG image="/image/plan_backdrop.png" />
        <BackButton onClick={onBackClick} style={{ top: '50px' }} />
        <div className={planStyle.planHeader}>
          <Typography type="h2" fontWeight={500} color={colors.darkText}>
            {t('purchase.planTitle', { plan: `${planName.slice(0, 3)} ${planName.slice(3)}` })}
          </Typography>
          <PurchaseSteps current={2} />
        </div>
        <div className={planStyle.planContent}>
          <Card raised className={planStyle.card}>
            {!emailSignup ? (
              <CardContent className={classNames(planStyle.cardContent, planStyle.cardContentSplit)}>
                <div className={planStyle.cardForm}>
                  <Typography
                    type="h5"
                    fontWeight={500}
                    fontSize={24}
                    color={colors.darkText}
                    lineHeight={getLineHeight(32)}
                    style={{ marginBottom: 24 }}>
                    {t('purchase.loginTitle')}
                  </Typography>
                  {(unauthorizedError || unexpectedError) && (
                    <div className={planStyle.error}>
                      <div className={planStyle.errorWrapper}>
                        <img alt={'info'} src={iconInfoLogin} style={{ marginRight: '15px', maxWidth: '24px' }} />
                        {unauthorizedError ? (
                          <div>
                            <Typography style={{ margin: 0 }} fontWeight={800} type={'medium'} color={colors.darkText}>
                              {t('purchase.inputInvalidError')}
                            </Typography>
                          </div>
                        ) : (
                          <div>
                            <Typography style={{ margin: 0 }} fontWeight={800} type={'medium'} color={colors.darkText}>
                              {t('purchase.generalError1')}
                            </Typography>
                            <Typography style={{ margin: '10px 0 0' }} type={'small'} color={colors.darkText}>
                              {t('purchase.generalError2')}
                            </Typography>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  <Formik
                    onSubmit={handleLogin}
                    initialValues={loginInitialValues}
                    validationSchema={loginValidationSchema}>
                    {({ values, isSubmitting }) => (
                      <Form>
                        <FormikInput name="email" label={t('purchase.fieldEmail')} />
                        <FormikInput name="password" label={t('purchase.fieldPassword')} type="password" />
                        <Button
                          loading={isSubmitting}
                          disabled={isSubmitting || !values.email || !values.password}
                          color="green"
                          title={t('purchase.loginButton')}
                          className={classNames('mt-4', 'full-width')}
                          type="submit"
                        />
                      </Form>
                    )}
                  </Formik>
                  <Typography
                    type="h6"
                    fontWeight={500}
                    fontSize={18}
                    color={colors.darkText}
                    style={{ margin: '24px 0' }}>
                    {t('purchase.orSeparator')}
                  </Typography>
                  <div className={planStyle.socialButtonContainer}>
                    <SocialLoginButtons onGoogle={googleSignin} onFacebook={facebookSignin} onApple={appleSignin} />
                  </div>
                </div>
                <div className={planStyle.cardSeparator} />
                <div className={planStyle.cardForm}>
                  <Typography
                    type="h5"
                    fontWeight={500}
                    fontSize={24}
                    color={colors.darkText}
                    lineHeight={getLineHeight(32)}
                    style={{ marginBottom: 24 }}>
                    {t('purchase.createAccountTitle')}
                  </Typography>
                  {existsError && (
                    <div className={planStyle.error}>
                      <div className={planStyle.errorWrapper}>
                        <img alt={'info'} src={iconInfoLogin} style={{ marginRight: '15px', maxWidth: '24px' }} />
                        <div>
                          <Typography style={{ margin: 0 }} fontWeight={800} type={'medium'} color={colors.darkText}>
                            {t('purchase.accountExistsError')}
                          </Typography>
                        </div>
                      </div>
                    </div>
                  )}
                  <Formik
                    onSubmit={handleEmail}
                    initialValues={emailInitialValues}
                    validationSchema={emailValidationSchema}>
                    {({ values, isSubmitting }) => (
                      <Form>
                        <FormikInput name="email" label={t('purchase.fieldEmail')} />
                        <Button
                          loading={isSubmitting}
                          disabled={isSubmitting || !values.email}
                          color="green"
                          title={t('purchase.createAccountButton')}
                          className={classNames('mt-4', 'full-width')}
                          type="submit"
                        />
                      </Form>
                    )}
                  </Formik>
                  <Typography
                    type="h6"
                    fontWeight={500}
                    fontSize={18}
                    color={colors.darkText}
                    style={{ margin: '24px 0' }}>
                    {t('purchase.orSeparator')}
                  </Typography>
                  <div className={planStyle.socialButtonContainer}>
                    <SocialLoginButtons onGoogle={googleSignup} onFacebook={facebookSignup} onApple={appleSignup} />
                  </div>
                  <div className={planStyle.termOfService}>
                    <Typography
                      fontSize={13}
                      fontWeight={400}
                      color={colors.darkText}
                      lineHeight="20px"
                      fontFamily={fonts.LATO}>
                      {t(`${ROOT_LOGIN}.confirm`)}
                      <br />
                      <Link href={'https://cleverday.com/agreement/'}>
                        <a>{t(`${ROOT_PURCHASE_PLAN}.proTerms`)}</a>
                      </Link>{' '}
                      {t(`${ROOT_LOGIN}.our`)}{' '}
                      <Link href={'https://cleverday.com/privacy/'}>
                        <a>{t(`${ROOT_PURCHASE_PLAN}.privacyPolicy`)}</a>
                      </Link>
                    </Typography>
                  </div>
                </div>
              </CardContent>
            ) : (
              <CardContent className={planStyle.cardContent}>
                <div className={planStyle.cardForm}>
                  <Typography type="h5" fontSize={24} fontWeight={500} color={colors.darkText}>
                    {t('purchase.welcome')}
                  </Typography>
                  <Typography fontSize={16} fontWeight={700} color={colors.darkText}>
                    {t('purchase.signupThanks')}
                  </Typography>
                  {(existsError || unexpectedError) && (
                    <div className={planStyle.error}>
                      <div className={planStyle.errorWrapper}>
                        <img alt={'info'} src={iconInfoLogin} style={{ marginRight: '15px', maxWidth: '24px' }} />
                        {existsError ? (
                          <div>
                            <Typography style={{ margin: 0 }} fontWeight={800} type={'medium'} color={colors.darkText}>
                              {t('purchase.accountExistsError')}
                            </Typography>
                          </div>
                        ) : (
                          <div>
                            <Typography style={{ margin: 0 }} fontWeight={800} type={'medium'} color={colors.darkText}>
                              {t('purchase.generalError1')}
                            </Typography>
                            <Typography style={{ margin: '10px 0 0' }} type={'small'} color={colors.darkText}>
                              {t('purchase.generalError2')}
                            </Typography>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  <Formik
                    onSubmit={handleSignup}
                    initialValues={signupInitialValues}
                    validationSchema={signupValidationSchema}>
                    {({ values, isSubmitting }) => (
                      <Form>
                        <FormikInput name="email" label={t('purchase.fieldEmail')} value={emailSignup} disabled />
                        <FormikInput name="fullName" label={t('purchase.fieldName')} />
                        <FormikInput name="password" type="password" label={t('purchase.fieldPassword')} />
                        <FormikInput name="confirm" type="password" label={t('purchase.fieldConfirm')} />
                        <Button
                          loading={isSubmitting}
                          disabled={isSubmitting || !values.fullName || !values.password || !values.confirm}
                          color="green"
                          title={t('purchase.continueButton')}
                          className={classNames('mt-4', 'full-width')}
                          type="submit"
                        />
                      </Form>
                    )}
                  </Formik>
                </div>
              </CardContent>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}

export const getServerSideProps: GetServerSideProps = async ({ req, res, query }) => {
  const user = await loggedInUser({ req });
  if (user) {
    return {
      redirect: {
        destination: `/account/upgrade?${queryGenerator(query)}`,
        permanent: false,
      },
    };
  }
  if (query.billing_cycle && query.plan) {
    return {
      redirect: {
        destination: `/signup_and_purchase?redirectUrl=${encodeURIComponent(
          `/account/upgrade?${queryGenerator(query)}`,
        )}`,
        permanent: false,
      },
    };
  }
  return {
    props: {
      description: '',
      namespacesRequired: ['common'],
    },
  };
};

export default SignupAndPurchase;
